import { SolutionPage } from "@/components/shared/SolutionPage";
import { OmnichannelVisual } from "@/components/shared/OmnichannelVisual";
import { Database, Calendar, Network, TrendingUp } from 'lucide-react';
import { Link } from "react-router-dom";
import { SystemLadder } from "@/components/shared/SystemLadder";
import { ServiceFAQ } from "@/components/shared/ServiceFAQ";

const CrmGrowth = () => {
  return (
    <SolutionPage
      breadcrumbTitle="CRM Growth"
      seo={{
        title: "CRM & Sales Automation | Frayze",
        description: "Stop losing leads to slow follow-up. Frayze CRM centralizes every inquiry, auto-responds in under 5 minutes, and turns pipeline chaos into booked revenue.",
        keywords: "CRM software, sales automation, lead management, business automation, automated follow-up, missed-call text-back",
        canonical: "https://frayze.ca/solutions/crm-automation/"
      }}
      hero={{
        badge: "Frayze Growth System: Step 2",
        badgeColor: "text-teal-700",
        title: "CRM That Automates Your Follow-Up.",
        description: "Stop losing leads to slow follow-up. We build and manage your automated text-backs, lead nurturing, and review requests. You just focus on the job.",
        buttonColor: "bg-teal-700 hover:bg-teal-800",
        gradientParams: "from-teal-500/5 via-background to-background",
        primaryCta: { text: "Simplify My Follow-Up", link: "/book-consultation/?tier=foundation" },
        secondaryCta: { text: "View Pricing", link: "/pricing/" },
        visual: <OmnichannelVisual />
      }}
      localStory={{
        eyebrow: "From a real roofing client",
        client: "Local roofing crew",
        location: "Local trades business",
        accentColor: "text-teal-700",
        story: (
          <>
            He was missing 4 calls a day while on job sites. Within 3 weeks of our setup, every missed call got an instant text-back, every quote got a 7-day follow-up, and every booked job auto-sent reminders without him touching his phone.
          </>
        ),
        result: "He booked 2 jobs that week he would have lost."
      }}
      problem={{
        title: "Most Businesses Don’t Lose Leads Because of Traffic",
        description: (
          <>
            They Lose Them Because of Delay. Missed calls. Late follow-ups. Spreadsheets no one checks. CRMs no one actually uses.
            <br /><br />
            <strong>These aren’t minor issues: they’re silent revenue killers.</strong>
          </>
        ),
        gridCols: 3,
        cards: [
          { value: "5 min", label: "Response window before conversion drops by 80%" },
          { value: "8x", label: "Follow-up attempts needed to reach a prospect (avg is 2)" },
          { value: "40%", label: "Of leads buy from the vendor that responds first" }
        ]
      }}
      systemModel={{
        title: "How the Foundation Works",
        description: "A Simple, Reliable Flow: From Chaos to Control.",
        variant: "steps",
        connectionColor: "bg-gradient-to-r from-teal-500/20 via-teal-500/50 to-teal-500/20",
        items: [
          { step: "1", title: "Capture", description: "Every lead source feeds into one unified pipeline." },
          { step: "2", title: "Organize", description: "Leads are auto-qualified and routed to the right person or team." },
          { step: "3", title: "Automate", description: "Instant SMS and email follow-ups fire automatically." },
          { step: "4", title: "Convert", description: "Bookings, reminders, and revenue tracking happen without manual work." }
        ]
      }}

      layers={{
        title: "What's Included in the Foundation",
        description: "High-Impact Workflows That Save Hours Every Week. This is the baseline, not an add-on.",
        theme: "teal",
        items: [
          {
            title: "Lead Management",
            icon: Database,
            items: [
              "Missed-call text-back",
              "7-day lead nurture sequence",
              "Visual pipeline stages"
            ]
          },
          {
            title: "Appointments & Reviews",
            icon: Calendar,
            items: [
              "Automated booking reminders",
              "No-show reactivation",
              "5-star review generation"
            ]
          }
        ]
      }}

      interdependence={{
        title: "Why the Foundation Matters",
        description: (
          <>
            CRM Growth is the database that powers every other Frayze system. <br />
            It tells the <Link to="/solutions/lead-generation-system/" className="font-bold underline decoration-indigo-500/50 hover:text-indigo-600 transition-colors">Marketing System</Link> which leads convert,
            tells the <Link to="/solutions/ai-strategy-rollout/" className="font-bold underline decoration-teal-500/50 hover:text-teal-600 transition-colors">Intelligence System</Link> who to call,
            and gives the <Link to="/solutions/local-seo-reputation/" className="font-bold underline decoration-blue-500/50 hover:text-blue-600 transition-colors">Visibility System</Link> somewhere reliable to send traffic.
            <br /><br />
            Learn why most CRM implementations fail in our <Link to="/blog/why-most-smb-crms-fail-90-days/" className="font-bold underline decoration-teal-500/50 hover:text-teal-600 transition-colors">90-Day Failure Analysis</Link>.
            <br /><br />
            The capabilities this foundation runs first: <Link to="/platform/missed-call-text-back/" className="font-bold underline decoration-teal-500/50 hover:text-teal-600 transition-colors">Missed-Call Text-Back</Link>, <Link to="/platform/database-reactivation/" className="font-bold underline decoration-emerald-500/50 hover:text-emerald-600 transition-colors">Database Reactivation</Link>, <Link to="/platform/reputation-management/" className="font-bold underline decoration-blue-500/50 hover:text-blue-600 transition-colors">Reputation Management</Link>, and the <Link to="/platform/business-phone-system/" className="font-bold underline decoration-sky-500/50 hover:text-sky-600 transition-colors">business phone system</Link> that feeds every call and text into this database.
          </>
        ),
        icon: Network,
        quote: "Without this foundation, marketing is just noise."
      }}
      useCases={{
        title: "Proven Across Industries",
        items: [
          { title: "Automotive", description: "Centralize leads from AutoTrader, website, finance apps, and walk-ins.", link: "/automotive/" },
          { title: "Trades & Contractors", description: "Auto-book estimates and automate job reminders.", link: "/industries/trades/" },
          { title: "Professional Services", description: "Long-term nurture and client lifecycle automation.", link: "/solutions/" }
        ]
      }}
      socialProof={{
        title: "Need A Car: +400% Appointment Rate",
        quote: "\"Before Frayze, our leads sat in emails. Now the system texts instantly. Our appointment rate quadrupled in 30 days.\"",
        icon: TrendingUp,
        ctaLink: "/case-studies/need-a-car-automotive-growth/"
      }}
      cta={{
        title: "Build Your Foundation",
        description: "Stop the leak. Systematize your sales. Scale with confidence.",
        primaryCta: { text: "Get Your Automation Score", link: "/digital-audit/" },
        secondaryCta: { text: "Explore Marketing System", link: "/solutions/lead-generation-system/" },
        color: "bg-teal-700 hover:bg-teal-800"
      }}
      customSection={
        <div className="space-y-24 pb-24">
          {/* Implementation Timeline */}
          <section className="bg-slate-50 dark:bg-slate-900/30 border-y" id="how-it-works">
            <div className="container mx-auto px-6 py-24">
              <div className="reveal text-center mb-16 max-w-3xl mx-auto">
                <h2 className="text-3xl font-bold mb-4">Implementation Timeline</h2>
                <p className="text-lg text-muted-foreground">From Chaos to Clarity in 3 Weeks</p>
              </div>
              <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                <div className="reveal lift-hover bg-background p-8 rounded-xl border shadow-sm relative">
                  <div className="absolute top-0 right-0 p-4 opacity-10 font-bold text-6xl text-teal-600">1</div>
                  <h3 className="text-xl font-bold mb-3 text-teal-700">Week 1: Architect</h3>
                  <p className="text-muted-foreground">We map your workflow, build pipelines, connect lead sources, and design follow-up logic.</p>
                </div>
                <div className="reveal reveal-delay-1 lift-hover bg-background p-8 rounded-xl border shadow-sm relative">
                  <div className="absolute top-0 right-0 p-4 opacity-10 font-bold text-6xl text-teal-600">2</div>
                  <h3 className="text-xl font-bold mb-3 text-teal-700">Week 2: Deploy</h3>
                  <p className="text-muted-foreground">Full technical setup: domains, deliverability, missed-call text-back, review widgets, and automations go live.</p>
                </div>
                <div className="reveal reveal-delay-2 lift-hover bg-background p-8 rounded-xl border shadow-sm relative">
                  <div className="absolute top-0 right-0 p-4 opacity-10 font-bold text-6xl text-teal-600">3</div>
                  <h3 className="text-xl font-bold mb-3 text-teal-700">Week 3: Optimize</h3>
                  <p className="text-muted-foreground">Live team training and optimization so every lead is handled correctly from day one.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Included Automations (Moved to Layers prop) */}

          {/* System Ladder Position */}
          <section className="container mx-auto px-6 pb-12">
            <div className="reveal text-center mb-12">
              <h2 className="text-2xl font-bold mb-4">Where This Fits in the Growth Ladder</h2>
            </div>
            <div className="reveal reveal-delay-1 bg-slate-50 dark:bg-slate-900/50 border rounded-2xl p-8 overflow-hidden max-w-5xl mx-auto">
              <SystemLadder activeTier="foundation" />
              <div className="text-center mt-8 text-muted-foreground text-sm font-medium">
                (No pressure: just clarity.)
              </div>
            </div>
          </section>

          <ServiceFAQ
            title="Questions About The Foundation"
            description="Common questions about moving from chaos to control."
            items={[
              {
                question: "Will this work with my existing email?",
                answer: "Yes. We integrate directly with Gmail, Outlook, and Office 365. You don't need to change your email address. We layer our tracking and automation on top of your existing communication flow."
              },
              {
                question: "Can I import my old contacts?",
                answer: "Absolutely. We provide a simple bulk import tool (CSV/Excel) and help you scrub your list during onboarding. We can even run a 'Database Reactivation' campaign to wake up those old leads immediately."
              },
              {
                question: "Is there a mobile app?",
                answer: "Yes. Your CRM comes with a full mobile app (iOS & Android). Your team can reply to texts, manage appointments, and view pipeline stages from the road."
              },
              {
                question: "How long is the contract?",
                answer: "Our CRM Growth plan is month-to-month. We believe the system should earn its keep every 30 days. Most clients stay for years because it becomes the operating system of their business."
              }
            ]}
          />
        </div>
      }
    />
  );
};

export default CrmGrowth;
